import React, { useState, useEffect } from "react";
import {
  Calendar,
  Users,
  DollarSign,
  BarChart3,
  Home,
  Settings,
  CreditCard,
  Wrench,
  LogOut,
  ChevronDown,
  ChevronRight,
  UserCheck,
  FileText,
  Building,
  Truck,
  Package,
  PackageCheck,
  Bot,
  FlaskConical,
  Shield,
  Calculator,
  Receipt,
  TrendingUp,
  Banknote,
  CheckSquare,
  PieChart,
  TrendingDown,
  CreditCard as CreditCardIcon,
  BookOpen,
  Phone,
  Lock,
  Info,
  ShieldCheck,
  User,
  Building2,
  Database,
  Target,
  Smile,
  MessageCircle,
  Megaphone,
  WalletCards,
  Link2,
  Gauge,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { hasPermission, permissionsForActiveClinic } from "@/lib/permissions";
import { useModulosSaas } from "@/hooks/useModulosSaas";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

const cadastroItems = [
  { title: "Dentistas", url: "/cadastros/dentistas", icon: UserCheck },
  { title: "Pacientes", url: "/patients", icon: Users },
  { title: "Planos de Tratamento", url: "/cadastros/planos-tratamento", icon: CheckSquare },
  { title: "Odontograma", url: "/cadastros/odontograma", icon: Smile },
  { title: "Ordem de Serviço (OS)", url: "/cadastros/os", icon: FileText },
  { title: "Funcionários", url: "/cadastros/funcionarios", icon: Building },
  { title: "Fornecedores", url: "/cadastros/fornecedores", icon: Truck },
  { title: "Patrimônio", url: "/cadastros/patrimonio", icon: Shield },
  { title: "Controle de estoque", url: "/cadastros/estoque", icon: Package },
  { title: "Laboratório", url: "/cadastros/laboratorio", icon: FlaskConical, modulo: "estoque_laboratorio" },
  { title: "Convênio / Planos", url: "/cadastros/convenios", icon: Shield },
  { title: "Tabela de Preços", url: "/cadastros/honorarios", icon: Calculator },
];

const financeiroItems = [
  { title: "Contas a Pagar", url: "/financeiro/contas-pagar", icon: Receipt },
  { title: "Contas a Receber", url: "/financeiro/contas-receber", icon: TrendingUp },
  { title: "Fluxo de Caixa", url: "/financeiro/fluxo-caixa", icon: Banknote },
  { title: "Controle de Pagamentos", url: "/financeiro/controle-pagamentos", icon: CreditCard, modulo: "controle_pagamentos" },
  { title: "Cobranças", url: "/financeiro/cobrancas", icon: WalletCards, modulo: "controle_pagamentos" },
  { title: "Metas e Comissões", url: "/relatorios/metas-comissoes", icon: Target, modulo: "relatorios_avancados" },
  { title: "Relatório", url: "/relatorios/financeiro", icon: BarChart3, modulo: "relatorios_avancados" },
];



const pagamentosItems = [
  { title: "Efetuar pagamento", url: "/pagamentos/efetuar-pagamento", icon: CreditCardIcon },
  { title: "Funcionários", url: "/pagamentos/funcionarios", icon: UserCheck, modulo: "pagamentos_funcionarios" },
];

const utilitariosItems = [
  { title: "Manuais e Códigos", url: "/utilitarios/manuais-codigos", icon: BookOpen },
  { title: "Contatos Úteis", url: "/utilitarios/contatos-uteis", icon: Phone },
  { title: "Modelos WhatsApp", url: "/utilitarios/whatsapp-modelos", icon: MessageCircle },
  { title: "Atendimento IA", url: "/utilitarios/atendimento-ia", icon: Bot, modulo: "atendimento_ia_whatsapp" },
  { title: "Campanhas e Comunicação", url: "/utilitarios/campanhas", icon: Megaphone, modulo: "atendimento_ia_whatsapp" },
  { title: "Atestados", url: "/utilitarios/atestados", icon: FileText, modulo: "documentos_atestados" },
  { title: "Contratos", url: "/utilitarios/contratos", icon: FileText, modulo: "documentos_atestados" },
  { title: "Portal do Paciente", url: "/utilitarios/portal-paciente", icon: Link2, modulo: "portal_paciente" },
  { title: "Backup e Exportação", url: "/utilitarios/backup", icon: Database },
];

const configuracoesItems = [
  { title: "Perfil", url: "/profile", icon: User },
  { title: "Senha do Administrador", url: "/configuracoes/senha-admin", icon: Lock },
  { title: "Informações da Clínica", url: "/configuracoes/informacoes-clinica", icon: Info },
  { title: "Permissões", url: "/configuracoes/permissoes", icon: ShieldCheck, superadminOnly: true },
];

const menuItems = [
  { title: "Inicio", url: "/dashboard", icon: Home },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const isCollapsed = state === "collapsed";

  // Route match helpers for submenu opening
  const cadastroMatch = currentPath.startsWith("/cadastros") || currentPath === "/patients";
  const financeiroMatch = currentPath.startsWith("/financeiro") || currentPath === "/relatorios/financeiro" || currentPath === "/relatorios/metas-comissoes";
  const pagamentosMatch = currentPath.startsWith("/pagamentos");
  const utilitariosMatch = currentPath.startsWith("/utilitarios");
  const configuracoesMatch = currentPath.startsWith("/configuracoes") || currentPath === "/profile";

  // Active state helpers - only true when the parent menu should be highlighted
  const isCadastroActive = false; // Parent menu never highlighted, only children
  const isFinanceiroActive = false; // Parent menu never highlighted, only children
  const isPagamentosActive = false; // Parent menu never highlighted, only children
  const isUtilitariosActive = false; // Parent menu never highlighted, only children
  const isConfiguracoesActive = false; // Parent menu never highlighted, only children

  // Initialize open state from current route to avoid flicker
  const [isCadastroOpen, setIsCadastroOpen] = useState(cadastroMatch);
  const [isFinanceiroOpen, setIsFinanceiroOpen] = useState(financeiroMatch);
  const [isPagamentosOpen, setIsPagamentosOpen] = useState(pagamentosMatch);
  const [isUtilitariosOpen, setIsUtilitariosOpen] = useState(utilitariosMatch);
  const [isConfiguracoesOpen, setIsConfiguracoesOpen] = useState(configuracoesMatch);

  const { signOut, user } = useAuth();
  const { toast } = useToast();
  const { hasModulo } = useModulosSaas();
  const selectedClinicaId = useActiveClinicaId();
  const isSuperAdmin = user?.role === "superadmin" || user?.email === "suporte@cloudservidores.com";
  const role = user?.role || user?.perfil;
  const activeClinicaId = selectedClinicaId || user?.clinica_id;
  const extraPermissions = permissionsForActiveClinic(user?.permissoes_extra || [], user?.permissoes_por_clinica, activeClinicaId);
  const can = (permission: string) => hasPermission(role, permission, extraPermissions);
  const canManageClinicas = can("clinicas:manage");
  const canUseAgenda = can("agenda:manage") || can("agenda:view");
  const canUseFinanceiro = can("financeiro:manage") || can("financeiro:view");
  const canUseRelatorios = can("relatorios:view") || can("financeiro:view") || can("financeiro:totais");
  const canUsePagamentos = can("financeiro:manage");
  const canUseEstoque = can("estoque:manage");
  const canUseCentral = can("gestao:view") || can("financeiro:manage") || canManageClinicas;

  const moduleAllowed = (item: { modulo?: string }) => !item.modulo || hasModulo(item.modulo);

  const visibleCadastroItems = cadastroItems.filter((item) => {
    if (!moduleAllowed(item)) return false;
    switch (item.url) {
      case "/cadastros/dentistas":
      case "/cadastros/funcionarios":
        return can("cadastros:manage") || can("usuarios:manage");
      case "/patients":
        return can("pacientes:view") || can("pacientes:manage") || can("cadastros:view") || can("cadastros:manage") || can("recibos:emit");
      case "/cadastros/planos-tratamento":
      case "/cadastros/odontograma":
        return can("clinico:manage") || can("cadastros:manage");
      case "/cadastros/os":
        return can("clinico:manage") || can("cadastros:manage") || can("ordem_servico:manage");
      case "/cadastros/fornecedores":
        return canUseEstoque || can("cadastros:manage") || can("fornecedores:manage");
      case "/cadastros/patrimonio":
        return canUseEstoque || can("cadastros:manage");
      case "/cadastros/estoque":
      case "/cadastros/laboratorio":
        return canUseEstoque;
      case "/cadastros/convenios":
      case "/cadastros/honorarios":
        return can("cadastros:view") || can("cadastros:manage");
      default:
        return can("cadastros:manage");
    }
  });
  const visibleFinanceiroItems = financeiroItems.filter((item) => {
    if (!moduleAllowed(item)) return false;
    if (item.url === "/relatorios/metas-comissoes" || item.url === "/relatorios/financeiro") return canUseRelatorios;
    return canUseFinanceiro;
  });
  const visiblePagamentosItems = pagamentosItems.filter((item) => moduleAllowed(item) && canUsePagamentos);
  const visibleUtilitariosItems = utilitariosItems.filter((item) => {
    if (!moduleAllowed(item)) return false;
    switch (item.url) {
      case "/utilitarios/manuais-codigos":
      case "/utilitarios/contatos-uteis":
        return true;
      case "/utilitarios/whatsapp-modelos":
      case "/utilitarios/atendimento-ia":
        return can("ia:manage") || can("clinicas:manage") || can("usuarios:manage");
      case "/utilitarios/campanhas":
        return !isSuperAdmin && can("campanhas:manage");
      case "/utilitarios/atestados":
        return can("atestados:manage");
      case "/utilitarios/contratos":
        return can("contratos:manage");
      case "/utilitarios/portal-paciente":
      case "/utilitarios/backup":
        return can("clinicas:manage") || can("usuarios:manage");
      default:
        return false;
    }
  });
  const visibleConfiguracoesItems = configuracoesItems.filter((item) => {
    if (item.superadminOnly) return isSuperAdmin;
    if (item.url === "/configuracoes/informacoes-clinica") return canManageClinicas;
    return true;
  });
  const canUseConfiguracoes = visibleConfiguracoesItems.length > 0;

  // Keep the matching group open on route change without closing others
  React.useLayoutEffect(() => {
    if (cadastroMatch) setIsCadastroOpen(true);
    if (financeiroMatch) setIsFinanceiroOpen(true);
    if (pagamentosMatch) setIsPagamentosOpen(true);
    if (utilitariosMatch) setIsUtilitariosOpen(true);
    if (configuracoesMatch) setIsConfiguracoesOpen(true);
  }, [cadastroMatch, financeiroMatch, pagamentosMatch, utilitariosMatch, configuracoesMatch]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Logout realizado",
        description: "Até logo!",
      });
      navigate("/auth", { replace: true });
    } catch (error) {
      toast({
        title: "Erro ao fazer logout",
        description: "Tente novamente",
        variant: "destructive",
      });
    }
  };

  const isActive = (path: string) => currentPath === path;
  
  const getNavClass = (_: { isActive: boolean }) => "hover:bg-muted/50";

  const getSubmenuNavClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "bg-primary/10 text-primary font-medium ml-4" : "text-sidebar-foreground hover:bg-muted/50 ml-4";
    
  const getParentNavClass = (_isChildActive: boolean) =>
    "hover:bg-muted/50";

  return (
    <Sidebar className={isCollapsed ? "w-14" : "w-64"} collapsible="icon">
      <SidebarHeader className="border-b border-border p-2 h-14 sm:h-16 flex items-center">
        <div className="flex items-center gap-3">
          <img src="/cloud-odonto-icon.png" alt="" className="h-8 w-8 flex-shrink-0 object-contain sm:h-9 sm:w-9" />
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg font-semibold text-foreground truncate">Cloud Odonto</h2>
              <p className="text-xs text-muted-foreground truncate">Sistema Odontológico</p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} end className={getNavClass}>
                      <item.icon className="w-4 h-4" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {isSuperAdmin ? (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink to="/superadmin" end className={getNavClass}>
                      <ShieldCheck className="w-4 h-4" />
                      {!isCollapsed && <span>SuperAdmin</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}

              {isSuperAdmin ? (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink to="/superadmin/modulos" end className={getNavClass}>
                      <PackageCheck className="w-4 h-4" />
                      {!isCollapsed && <span>Módulos SaaS</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : canManageClinicas ? (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <NavLink to="/cliente/clinicas" end className={getNavClass}>
                        <Building2 className="w-4 h-4" />
                        {!isCollapsed && <span>Minhas Clínicas</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <NavLink to="/cliente/modulos" end className={getNavClass}>
                        <PackageCheck className="w-4 h-4" />
                        {!isCollapsed && <span>Módulos</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              ) : null}

              {hasModulo("crm") && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink to="/crm" end className={getNavClass}>
                    <MessageCircle className="w-4 h-4" />
                    {!isCollapsed && <span>CRM</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              )}

              {canUseCentral && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink to="/gestao/central" end className={getNavClass}>
                      <Gauge className="w-4 h-4" />
                      {!isCollapsed && <span>Central de gestão</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Agenda */}
              {canUseAgenda && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink to="/appointments" end className={getNavClass}>
                    <Calendar className="w-4 h-4" />
                    {!isCollapsed && <span>Agenda</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              )}
              
              {/* Cadastros with submenu */}
              {visibleCadastroItems.length > 0 && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setIsCadastroOpen(!isCadastroOpen)}
                  className={getParentNavClass(isCadastroActive)}
                >
                  <Users className={isCadastroActive ? "w-4 h-4 text-primary" : "w-4 h-4"} />
                  {!isCollapsed && (
                    <>
                      <span className={isCadastroActive ? "text-primary" : undefined}>Cadastros</span>
                      {isCadastroOpen ? (
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      )}
                    </>
                  )}
                </SidebarMenuButton>
                
                {/* Submenu */}
                {isCadastroOpen && !isCollapsed && (
                  <SidebarMenuSub>
                    {visibleCadastroItems.map((item) => (
                      <SidebarMenuSubItem key={item.title}>
                        <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                          <NavLink to={item.url} end>
                            <item.icon className="w-3 h-3" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              )}
              
              {/* Financeiro with submenu */}
              {visibleFinanceiroItems.length > 0 && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setIsFinanceiroOpen(!isFinanceiroOpen)}
                  className={getParentNavClass(isFinanceiroActive)}
                >
                  <DollarSign className={isFinanceiroActive ? "w-4 h-4 text-primary" : "w-4 h-4"} />
                  {!isCollapsed && (
                    <>
                      <span className={isFinanceiroActive ? "text-primary" : undefined}>Financeiro</span>
                      {isFinanceiroOpen ? (
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      )}
                    </>
                  )}
                </SidebarMenuButton>
                
                {/* Submenu */}
                {isFinanceiroOpen && !isCollapsed && (
                  <SidebarMenuSub>
                    {visibleFinanceiroItems.map((item) => (
                      <SidebarMenuSubItem key={item.title}>
                        <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                          <NavLink to={item.url} end>
                            <item.icon className="w-3 h-3" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              )}
              

              
              {/* Pagamentos with submenu */}
              {canUsePagamentos && visiblePagamentosItems.length > 0 && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setIsPagamentosOpen(!isPagamentosOpen)}
                  className={getParentNavClass(isPagamentosActive)}
                >
                  <CreditCard className={isPagamentosActive ? "w-4 h-4 text-primary" : "w-4 h-4"} />
                  {!isCollapsed && (
                    <>
                      <span className={isPagamentosActive ? "text-primary" : undefined}>Pagamentos</span>
                      {isPagamentosOpen ? (
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      )}
                    </>
                  )}
                </SidebarMenuButton>
                
                {/* Submenu */}
                {isPagamentosOpen && !isCollapsed && (
                  <SidebarMenuSub>
                    {visiblePagamentosItems.map((item) => (
                      <SidebarMenuSubItem key={item.title}>
                        <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                          <NavLink to={item.url} end>
                            <item.icon className="w-3 h-3" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              )}
              
              {/* Utilitários with submenu */}
              {visibleUtilitariosItems.length > 0 && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setIsUtilitariosOpen(!isUtilitariosOpen)}
                  className={getParentNavClass(isUtilitariosActive)}
                >
                  <Wrench className={isUtilitariosActive ? "w-4 h-4 text-primary" : "w-4 h-4"} />
                  {!isCollapsed && (
                    <>
                      <span className={isUtilitariosActive ? "text-primary" : undefined}>Utilitários</span>
                      {isUtilitariosOpen ? (
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      )}
                    </>
                  )}
                </SidebarMenuButton>
                
                {/* Submenu */}
                {isUtilitariosOpen && !isCollapsed && (
                  <SidebarMenuSub>
                    {visibleUtilitariosItems.map((item) => (
                      <SidebarMenuSubItem key={item.title}>
                        <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                          <NavLink to={item.url} end>
                            <item.icon className="w-3 h-3" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              )}
              
              {/* Configurações with submenu */}
              {canUseConfiguracoes && visibleConfiguracoesItems.length > 0 && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setIsConfiguracoesOpen(!isConfiguracoesOpen)}
                  className={getParentNavClass(isConfiguracoesActive)}
                >
                  <Settings className={isConfiguracoesActive ? "w-4 h-4 text-primary" : "w-4 h-4"} />
                  {!isCollapsed && (
                    <>
                      <span className={isConfiguracoesActive ? "text-primary" : undefined}>Configurações</span>
                      {isConfiguracoesOpen ? (
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      )}
                    </>
                  )}
                </SidebarMenuButton>
                
                {/* Submenu */}
                {isConfiguracoesOpen && !isCollapsed && (
                  <SidebarMenuSub>
                    {visibleConfiguracoesItems.map((item) => (
                      <SidebarMenuSubItem key={item.title}>
                        <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                          <NavLink to={item.url} end>
                            <item.icon className="w-3 h-3" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Fixed logout button at bottom */}
      <div className="mt-auto border-t border-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={handleSignOut}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 cursor-pointer mobile-touch-target"
            >
              <LogOut className="w-4 h-4" />
              {!isCollapsed && <span>Desconectar</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </div>
    </Sidebar>
  );
}
